/**
 * Desktop session bridge for native voice.
 *
 * Hands the user's Supabase session to the native (Tauri/Rust) shell so Rust can mint
 * the Deepgram token itself — keeping the overlay WebView off the voice hot path. Inert
 * in a normal browser (`window.__TAURI__` is undefined), so this is a no-op on the web.
 *
 * We pass the USER session (access + refresh + the public anon key) and the URLs Rust
 * needs to refresh + mint. NEVER the Supabase service key — that bypasses RLS and must
 * never reach a client.
 */
import type { Session } from "@supabase/supabase-js";
import { SERVER_URL } from "@/lib/api/base";

interface TauriCore {
  invoke<T = unknown>(cmd: string, args?: Record<string, unknown>): Promise<T>;
}
interface TauriEvent {
  listen(event: string, handler: () => void): Promise<() => void>;
}
interface BridgeWindow extends Window {
  __TAURI__?: { core?: TauriCore; event?: TauriEvent };
}

/**
 * Native asks for a session re-push when its cached copy dies. Supabase
 * rotates refresh tokens on every use, so the shell's private copy can lose
 * the rotation race to this web client (07-27: PTT flicker, native refresh
 * 400s). The web client is the single legitimate rotator — on this event the
 * AuthContext re-pushes its CURRENT session. Inert in a normal browser.
 * Returns an unsubscribe.
 */
export function watchNativeSessionStale(onStale: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listen = (window as BridgeWindow).__TAURI__?.event?.listen;
  if (!listen) return () => {};
  let unlisten: (() => void) | null = null;
  let disposed = false;
  void listen("vestra://voice-session-stale", onStale)
    .then((un) => {
      if (disposed) un();
      else unlisten = un;
    })
    .catch(() => {});
  return () => {
    disposed = true;
    unlisten?.();
  };
}

export function pushVoiceSessionToNative(session: Session | null): void {
  if (typeof window === "undefined") return;
  const core = (window as BridgeWindow).__TAURI__?.core;
  if (!core) return;
  if (!session) {
    // Signed out: the shell drops its cached session and hides its signed-in
    // surfaces (the notch fleet) until the next sign-in.
    void core.invoke("voice_clear_session").catch(() => {});
    return;
  }

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !anonKey) return;

  void core
    .invoke("voice_set_session", {
      session: {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: Math.floor(session.expires_at ?? 0),
        supabase_url: supabaseUrl,
        anon_key: anonKey,
        server_url: SERVER_URL,
      },
    })
    .catch(() => {});
}
